import { useEffect, useState } from "react";
import api from "./api";

/**
 * Brief 3.5 PR 3 — single source of truth verdict hook.
 *
 * Wraps GET /admin/merchants/{id}/verdict. One call per mount per
 * merchantId. Backend caches 30s, so this is cheap even when multiple
 * surfaces re-read after a refresh.
 *
 * Consumers: ReReviewRibbon, RecommendationBanner, CrossCheckSummary.
 * AdminMerchantDetail.refresh() bumps `reloadKey` to force a re-fetch.
 *
 * Returned shape: { verdict, loading, error }.
 *   • verdict: full payload from the server (icon/tone/primary_blocker/
 *     counts/severity_counts/screening_counts/per_doc/rules_version),
 *     or null while loading / on error.
 *   • loading: true on initial mount and on any reloadKey bump.
 *   • error: response error object on failure, null otherwise.
 */
export default function useMerchantVerdict(merchantId, reloadKey = 0) {
  const [state, setState] = useState({
    verdict: null, loading: true, error: null,
  });

  useEffect(() => {
    if (!merchantId) return undefined;
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));
    api.get(`/admin/merchants/${merchantId}/verdict`)
      .then(({ data }) => {
        if (!cancelled) {
          setState({ verdict: data, loading: false, error: null });
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setState({ verdict: null, loading: false, error: err });
        }
      });
    return () => { cancelled = true; };
  }, [merchantId, reloadKey]);

  return state;
}
